// frontend/src/features/ups/modals/CreateAgencyWizardModal.js
// UPS Admin: Create Agency Wizard Modal
// - Step 1: Create agency profile
// - Step 2: Optionally create first agency login
// - Step 3: Review and submit
// Uses UPS theme tokens and a mobile-safe modal shell:
// fixed header/stepper/footer inside modal, scrollable body on small screens.

import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { createAgency } from '../../agencies/agenciesSlice';
import { createAccount } from '../../agencyAccounts/agencyAccountsSlice';

const inputClass =
  'w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20';

const labelClass = 'mb-1.5 block text-sm font-semibold text-app-text';

const secondaryButtonClass =
  'rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60';

const primaryButtonClass =
  'rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60';

function StepPill({ number, label, active }) {
  return (
    <li
      className={`flex min-w-[7.5rem] items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold transition sm:min-w-0 ${
        active
          ? 'border-app-primary bg-app-primary text-white shadow-sm'
          : 'border-app-border bg-app-card text-app-muted'
      }`}
    >
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${
          active ? 'bg-white/20 text-white' : 'bg-app-surface text-app-muted'
        }`}
      >
        {number}
      </span>
      <span className="truncate">{label}</span>
    </li>
  );
}

export default function CreateAgencyWizardModal({ isOpen, onClose }) {
  const dispatch = useDispatch();

  const agencyNameId = 'create-agency-name';
  const maxActiveId = 'create-agency-max-active';
  const makeLoginId = 'create-agency-make-login';
  const roleId = 'create-agency-role';
  const usernameId = 'create-agency-username';
  const emailId = 'create-agency-email';
  const passwordId = 'create-agency-password';

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // ---------------- Step 1: Agency ----------------
  const [name, setName] = useState('');
  const [maxActive, setMaxActive] = useState('');

  // ---------------- Step 2: First account ----------------
  const [makeLogin, setMakeLogin] = useState(true);
  const [role, setRole] = useState('agency_admin');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const canNext = useMemo(() => name.trim().length > 0, [name]);

  const canCreate = useMemo(() => {
    if (!makeLogin) return true;
    return username.trim() && email.trim() && password;
  }, [makeLogin, username, email, password]);

  const reset = () => {
    setStep(1);
    setBusy(false);
    setError('');
    setName('');
    setMaxActive('');
    setMakeLogin(true);
    setRole('agency_admin');
    setUsername('');
    setEmail('');
    setPassword('');
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (busy) return;

    setBusy(true);
    setError('');

    try {
      const payload = { name: name.trim() };

      if (maxActive) {
        payload.max_active_drivers = Number(maxActive);
      }

      const agency = await dispatch(createAgency(payload)).unwrap();

      if (makeLogin) {
        await dispatch(
          createAccount({
            agencyId: agency.id,
            role,
            username: username.trim(),
            email: email.trim(),
            password,
          })
        ).unwrap();
      }

      reset();
      onClose();
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to create agency');
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={handleClose}
    >
      <div
      className="flex h-[78dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---------------- Header ---------------- */}
        <div className="shrink-0 border-b border-app-border bg-app-surface px-5 py-3 sm:py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-app-muted">
                Agency setup
              </p>
              <h3 className="mt-1 text-lg font-extrabold text-app-text">
                Create agency
              </h3>
              <p className="mt-1 hidden text-sm text-app-muted sm:block">
                Add a new agency and optionally create their first login.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="rounded-xl p-2 text-app-muted transition hover:bg-app-card hover:text-app-text disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Close create agency modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ---------------- Stepper ---------------- */}
        <div className="shrink-0 border-b border-app-border px-4 py-3 sm:px-5 sm:py-4">
          <ol className="flex gap-2 overflow-x-auto pb-1 sm:grid sm:grid-cols-3 sm:overflow-visible sm:pb-0">
            <StepPill number="1" label="Agency" active={step >= 1} />
            <StepPill number="2" label="First account" active={step >= 2} />
            <StepPill number="3" label="Review" active={step >= 3} />
          </ol>
        </div>

        {/* ---------------- Body: only this section scrolls on mobile ---------------- */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          {step === 1 && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-app-border bg-app-surface p-4">
                <h4 className="text-sm font-bold text-app-text">
                  Agency details
                </h4>
                <p className="mt-1 text-sm text-app-muted">
                  This creates the agency record used for contracts, accounts,
                  and driver ownership.
                </p>

                <div className="mt-4 space-y-4">
                  <div>
                    <label htmlFor={agencyNameId} className={labelClass}>
                      Agency name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={agencyNameId}
                      name="agencyName"
                      className={inputClass}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Example: North London Couriers"
                    />
                  </div>

                  <div>
                    <label htmlFor={maxActiveId} className={labelClass}>
                      Max active drivers
                    </label>
                    <input
                      id={maxActiveId}
                      name="maxActiveDrivers"
                      className={inputClass}
                      type="number"
                      min="0"
                      value={maxActive}
                      onChange={(e) => setMaxActive(e.target.value)}
                      placeholder="Optional"
                    />
                    <p className="mt-1 text-xs text-app-muted">
                      Optional limit for how many active drivers this agency can
                      have.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-app-border bg-app-surface p-4">
                <div className="flex items-start gap-3">
                  <input
                    id={makeLoginId}
                    name="makeLogin"
                    type="checkbox"
                    checked={makeLogin}
                    onChange={(e) => setMakeLogin(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-app-border text-app-primary focus:ring-app-primary"
                  />

                  <div>
                    <label
                      htmlFor={makeLoginId}
                      className="text-sm font-bold text-app-text"
                    >
                      Create first login for this agency
                    </label>
                    <p className="mt-1 text-sm text-app-muted">
                      Useful when the agency needs access immediately after
                      setup.
                    </p>
                  </div>
                </div>
              </div>

              {makeLogin && (
                <div className="rounded-2xl border border-app-border bg-app-surface p-4">
                  <h4 className="text-sm font-bold text-app-text">
                    First account
                  </h4>
                  <p className="mt-1 text-sm text-app-muted">
                    This creates the first user account under the new agency.
                  </p>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor={roleId} className={labelClass}>
                        Role
                      </label>
                      <select
                        id={roleId}
                        name="role"
                        className={inputClass}
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                      >
                        <option value="agency_admin">Agency Admin</option>
                        <option value="agency_user">Agency User</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor={usernameId} className={labelClass}>
                        Username
                      </label>
                      <input
                        id={usernameId}
                        name="username"
                        className={inputClass}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="agency.admin"
                      />
                    </div>

                    <div>
                      <label htmlFor={emailId} className={labelClass}>
                        Email
                      </label>
                      <input
                        id={emailId}
                        name="email"
                        className={inputClass}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor={passwordId} className={labelClass}>
                        Password
                      </label>
                      <input
                        id={passwordId}
                        name="password"
                        className={inputClass}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Temporary password"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-app-border bg-app-surface p-4">
                <h4 className="text-sm font-bold text-app-text">
                  Review agency
                </h4>

                <dl className="mt-4 divide-y divide-app-border rounded-xl border border-app-border bg-app-card text-sm">
                  <div className="flex items-center justify-between gap-4 px-4 py-3">
                    <dt className="font-semibold text-app-muted">Agency</dt>
                    <dd className="text-right font-bold text-app-text">
                      {name || '—'}
                    </dd>
                  </div>

                  <div className="flex items-center justify-between gap-4 px-4 py-3">
                    <dt className="font-semibold text-app-muted">
                      Max active
                    </dt>
                    <dd className="text-right text-app-text">
                      {maxActive || 'No limit set'}
                    </dd>
                  </div>

                  <div className="flex items-center justify-between gap-4 px-4 py-3">
                    <dt className="font-semibold text-app-muted">
                      Create first login
                    </dt>
                    <dd className="text-right text-app-text">
                      {makeLogin ? 'Yes' : 'No'}
                    </dd>
                  </div>
                </dl>
              </div>

              {makeLogin && (
                <div className="rounded-2xl border border-app-border bg-app-surface p-4">
                  <h4 className="text-sm font-bold text-app-text">
                    Review first account
                  </h4>

                  <dl className="mt-4 divide-y divide-app-border rounded-xl border border-app-border bg-app-card text-sm">
                    <div className="flex items-center justify-between gap-4 px-4 py-3">
                      <dt className="font-semibold text-app-muted">Role</dt>
                      <dd className="text-right text-app-text">{role}</dd>
                    </div>

                    <div className="flex items-center justify-between gap-4 px-4 py-3">
                      <dt className="font-semibold text-app-muted">
                        Username
                      </dt>
                      <dd className="text-right text-app-text">
                        {username || '—'}
                      </dd>
                    </div>

                    <div className="flex items-center justify-between gap-4 px-4 py-3">
                      <dt className="font-semibold text-app-muted">Email</dt>
                      <dd className="text-right text-app-text">
                        {email || '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* ---------------- Footer ---------------- */}
        <div
          className="shrink-0 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className={secondaryButtonClass}
            >
              Cancel
            </button>

            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  disabled={busy}
                  className={secondaryButtonClass}
                >
                  Back
                </button>
              )}

              {step < 3 && (
                <button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={(step === 1 && !canNext) || (step === 2 && !canCreate)}
                  className={primaryButtonClass}
                >
                  Next
                </button>
              )}

              {step === 3 && (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={busy}
                  className={primaryButtonClass}
                >
                  {busy ? 'Creating…' : 'Create agency'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}